import { useEffect, useMemo, useState } from "react";
import { haversineMeters } from "../utils/locationSharing";
import { useMockLocation } from "../contexts/MockLocationContext";
import { useLocationTracking } from "../contexts/LocationTrackingContext";

/**
 * How old a shared point (from the watch or the native phone service) may be
 * and still stand in for this device's own fix.
 */
const SHARED_POINT_MAX_AGE_MS = 15 * 60_000;

export interface NearbyResult<T> {
  entity: T;
  distanceMeters: number;
}

interface LocatableEntity {
  location: { latitude: number; longitude: number } | null;
}

interface UseNearbyEntitiesOptions {
  /** Don't request the device location until this is true (default false). */
  enabled?: boolean;
  /** Max results returned, nearest first (default 10, matching the Wear OS app). */
  limit?: number;
  /** Discard entities farther away than this many metres. */
  maxDistanceMeters?: number;
}

/**
 * Client-side "what's near me" lookup: grabs the device position once (when
 * `enabled`), then ranks the supplied entities by great-circle distance. Used
 * to power "start your next activity at the closest place" suggestions.
 *
 * When this device can't produce a fix (denied, timed out, or the Android
 * app's Chrome can't hand location over), the newest point shared from the
 * watch or the native phone service stands in, as long as it's recent.
 */
export function useNearbyEntities<T extends LocatableEntity>(
  entities: T[],
  options: UseNearbyEntitiesOptions = {},
) {
  const { enabled = false, limit = 10, maxDistanceMeters } = options;
  const { mock } = useMockLocation();
  const { latestPoint } = useLocationTracking();
  const [devicePosition, setDevicePosition] = useState<{ lat: number; lng: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // A test override short-circuits the device geolocation entirely (and
    // never triggers a permission prompt).
    if (mock) {
      setError(null);
      setLoading(false);
      return;
    }
    if (!enabled) return;
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      setError("Location isn't available on this device.");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        setDevicePosition({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        setError(
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied."
            : err.message || "Couldn't read your location.",
        );
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, mock]);

  // Re-read the clock whenever the shared point changes, so freshness is
  // judged against when we heard it rather than a stale render.
  const [sharedHeardAt, setSharedHeardAt] = useState(() => Date.now());
  useEffect(() => {
    setSharedHeardAt(Date.now());
  }, [latestPoint]);

  const sharedPosition = useMemo(
    () =>
      latestPoint && sharedHeardAt - latestPoint.t <= SHARED_POINT_MAX_AGE_MS
        ? { lat: latestPoint.lat, lng: latestPoint.lng }
        : null,
    [latestPoint, sharedHeardAt],
  );

  const position = useMemo(
    () => (mock ? { lat: mock.lat, lng: mock.lng } : (devicePosition ?? sharedPosition)),
    [mock, devicePosition, sharedPosition],
  );

  const nearby = useMemo<NearbyResult<T>[]>(() => {
    if (!position) return [];
    const results: NearbyResult<T>[] = [];
    for (const entity of entities) {
      const loc = entity.location;
      if (!loc || typeof loc.latitude !== "number" || typeof loc.longitude !== "number") continue;
      const distanceMeters = haversineMeters(
        position.lat,
        position.lng,
        loc.latitude,
        loc.longitude,
      );
      if (maxDistanceMeters != null && distanceMeters > maxDistanceMeters) continue;
      results.push({ entity, distanceMeters });
    }
    results.sort((a, b) => a.distanceMeters - b.distanceMeters);
    return results.slice(0, limit);
  }, [position, entities, limit, maxDistanceMeters]);

  // A usable shared point answers the question, so neither the spinner nor
  // this device's own failure needs showing.
  const usingShared = !mock && !devicePosition && !!sharedPosition;
  return {
    nearby,
    position,
    loading: usingShared ? false : loading,
    error: usingShared ? null : error,
  };
}
